import { useReportTask } from '@/features/nectar/api'
import { Button, Spinner } from '@/ui-kit'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui-kit/components/Dialog'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { useState } from 'react'
import { toast } from 'sonner'

const PRESET_REASONS = [
  { id: 'incorrect', label: msg`Content is incorrect or unnatural` },
  { id: 'inappropriate', label: msg`Content is inappropriate or offensive` },
  { id: 'unclear', label: msg`Instructions are unclear or confusing` }
] as const

type Props = {
  open: boolean
  onClose: () => void
  projectId: string
  taskId: string
  userId: string
}

export const TaskReportModal = ({ open, onClose, projectId, taskId, userId }: Props) => {
  const { t } = useLingui()
  const [selected, setSelected] = useState<string | null>(null)
  const [custom, setCustom] = useState('')
  const { mutateAsync: reportTask, isPending } = useReportTask()

  const reason = selected === 'custom' ? custom.trim() : selected
  const canSubmit = !!reason && !isPending

  const handleClose = () => {
    if (isPending) return
    setSelected(null)
    setCustom('')
    onClose()
  }

  const handleSubmit = async () => {
    if (!reason) return
    try {
      await reportTask({ projectId, taskId, userId, reason })
      toast.success(t`Report submitted. Thank you for your feedback.`)
      handleClose()
    } catch {
      toast.error(t`Failed to submit report. Please try again.`)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose()
      }}
    >
      <DialogContent className="flex flex-col gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[24px]/[32px]">
            <Trans>Report an issue</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {PRESET_REASONS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSelected(id)}
              className={clsx(
                'text-body-2 rounded-xl border border-solid px-4 py-3 text-left transition-colors outline-none',
                selected === id
                  ? 'border-orange bg-orange/10 text-gray-70'
                  : 'hover:border-gray-90 text-gray-70 border-gray-100'
              )}
            >
              {t(label)}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setSelected('custom')}
            className={clsx(
              'text-body-2 rounded-xl border border-solid px-4 py-3 text-left transition-colors outline-none',
              selected === 'custom'
                ? 'border-orange bg-orange/10 text-gray-70'
                : 'hover:border-gray-90 text-gray-70 border-gray-100'
            )}
          >
            <Trans>Other (describe the issue)</Trans>
          </button>

          {selected === 'custom' && (
            <textarea
              autoFocus
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={t`Describe the issue...`}
              rows={3}
              className="text-body-2 backgroundColorBlackToWhite text-gray-70 placeholder:text-gray-80 focus:border-gray-90 mt-1 w-full resize-none rounded-xl border border-solid border-gray-100 p-4 outline-none"
            />
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="bordered-gray" className="flex-1" onClick={handleClose} isDisabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button className="flex-1" onClick={handleSubmit} isDisabled={!canSubmit}>
            {isPending && <Spinner size="small" />}
            <Trans>Submit</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
